import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '@/supabase/client'
import { useAuth } from '@/lib/auth-context'
import AddStudentModal from '@/components/teacher/AddStudentModal'
import { useTeacherBalance } from '@/hooks/useTeacherBalance'
import { jsonFetch } from '@/utils/jsonFetch'
import { ParentUpdatesCard } from '@/components/teacher/ParentUpdatesCard'
import { CurrentToolsCard } from '@/components/ai/CurrentToolsCard'

const CORE_API = import.meta.env.VITE_CORE_API_URL || ''

interface Reimbursement {
  id: string;
  status?: string;
  caption?: string;
  created_at?: string;
}

interface PhotoPreview {
  id: string;
  file_url: string;
  caption?: string;
  approved?: boolean;
  previewUrl: string | null;
}

interface Student {
  id: string;
  full_name: string;
  display_name?: string;
  grade?: string | null;
  status?: 'draft' | 'pending' | 'active' | 'archived' | null;
  guardian_confirmed_at?: string | null;
}

interface InviteInfo {
  id: string;
  email: string | null;
  phone: string | null;
  relationship: string | null;
  status: string;
  expired: boolean;
  expires_at: string | null;
  accepted_at: string | null;
  created_at: string | null;
}

const STATUS_THEME: Record<string, string> = {
  submitted: 'bg-sand text-cocoa/80',
  approved: 'bg-emerald-100 text-emerald-900',
  reimbursed: 'bg-sky-100 text-sky-900',
  rejected: 'bg-brick/10 text-brick',
}

const PHOTO_STATUS_THEME: Record<string, string> = {
  approved: 'bg-emerald-100 text-emerald-800',
  pending: 'bg-sand text-cocoa/80',
}

const STUDENT_STATUS_THEME: Record<string, { bg: string; label: string }> = {
  draft: { bg: 'bg-gray-100 text-gray-700', label: 'Draft' },
  pending: { bg: 'bg-amber-100 text-amber-800', label: 'Pending guardian' },
  active: { bg: 'bg-emerald-100 text-emerald-800', label: 'Active' },
  archived: { bg: 'bg-slate-100 text-slate-600', label: 'Archived' },
}

function formatStatus(value: string | null | undefined): string {
  if (!value) return 'Pending'
  const normalized = String(value).toLowerCase()
  return normalized.charAt(0).toUpperCase() + normalized.slice(1)
}

function formatDate(value: string | null | undefined): string {
  if (!value) return ''
  try {
    return new Date(value).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    })
  } catch (error) {
    return ''
  }
}

// ── Student Row with expandable invite details ─────────────────────────

function StudentRow({ student, onRefresh }: { student: Student; onRefresh: () => void }) {
  const navigate = useNavigate()
  const [expanded, setExpanded] = useState(false)
  const [invites, setInvites] = useState<InviteInfo[]>([])
  const [invitesLoading, setInvitesLoading] = useState(false)
  const [resending, setResending] = useState(false)
  const [resendResult, setResendResult] = useState<string | null>(null)

  const statusKey = student.status || 'draft'
  const statusTheme = STUDENT_STATUS_THEME[statusKey] || STUDENT_STATUS_THEME.draft

  const loadInvites = useCallback(async () => {
    setInvitesLoading(true)
    try {
      const raw = await jsonFetch(`${CORE_API}/api/v1/students/${student.id}/invites`) as any
      const data = raw?.data ?? raw
      setInvites(Array.isArray(data?.invites) ? data.invites : [])
    } catch {
      setInvites([])
    } finally {
      setInvitesLoading(false)
    }
  }, [student.id])

  const handleExpand = () => {
    const next = !expanded
    setExpanded(next)
    if (next && invites.length === 0) loadInvites()
  }

  const handleResend = async (inviteId?: string) => {
    setResending(true)
    setResendResult(null)
    try {
      const raw = await jsonFetch(`${CORE_API}/api/v1/students/${student.id}/resend-invite`, {
        method: 'POST',
        body: JSON.stringify(inviteId ? { invite_id: inviteId } : {}),
      }) as any
      const data = raw?.data ?? raw
      if (data?.success) {
        setResendResult(`Invite resent to ${data.email || 'guardian'}`)
        loadInvites()
        onRefresh()
      } else {
        setResendResult(raw?.error?.message || data?.error || 'Failed to resend')
      }
    } catch (err) {
      setResendResult(err instanceof Error ? err.message : 'Failed to resend')
    } finally {
      setResending(false)
    }
  }

  return (
    <div className="rounded-xl border border-white/60 bg-white/50 overflow-hidden">
      {/* Main row — clickable to expand */}
      <button
        type="button"
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-white/70 transition-colors"
        onClick={handleExpand}
      >
        <span
          className="flex-1 font-medium text-cocoa hover:text-brick hover:underline cursor-pointer"
          onClick={(e) => { e.stopPropagation(); navigate(`/portal/teacher/students/${student.id}`); }}
          role="link"
        >
          {student.full_name}
        </span>
        <span className="text-sm text-cocoa/60">{student.grade ?? '—'}</span>
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusTheme.bg}`}>
          {statusTheme.label}
        </span>
        <svg
          className={`h-4 w-4 text-cocoa/40 transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Expanded detail panel */}
      {expanded && (
        <div className="border-t border-white/60 bg-white/30 px-4 py-3 space-y-3">
          {invitesLoading ? (
            <p className="text-xs text-cocoa/50">Loading invite details...</p>
          ) : invites.length === 0 ? (
            <p className="text-xs text-cocoa/50">No guardian invites sent yet.</p>
          ) : (
            invites.map((inv) => (
              <div key={inv.id} className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between rounded-lg bg-white/60 px-3 py-2">
                <div className="space-y-0.5">
                  <p className="text-sm text-cocoa">
                    {inv.email || inv.phone || 'No contact'}
                    {inv.relationship ? <span className="ml-2 text-xs text-cocoa/50">({inv.relationship})</span> : null}
                  </p>
                  <p className="text-xs text-cocoa/50">
                    {inv.status === 'accepted'
                      ? `Accepted ${inv.accepted_at ? formatDate(inv.accepted_at) : ''}`
                      : inv.expired
                        ? 'Expired'
                        : `Pending — expires ${inv.expires_at ? formatDate(inv.expires_at) : 'soon'}`
                    }
                  </p>
                </div>
                {inv.status !== 'accepted' && (
                  <button
                    type="button"
                    className="rounded bg-brick px-3 py-1 text-xs font-semibold text-white hover:bg-brickDark disabled:opacity-50 whitespace-nowrap"
                    onClick={(e) => { e.stopPropagation(); handleResend(inv.id); }}
                    disabled={resending}
                  >
                    {resending ? 'Sending...' : inv.expired ? 'Resend invite' : 'Resend'}
                  </button>
                )}
              </div>
            ))
          )}
          {resendResult && (
            <p className={`text-xs ${resendResult.startsWith('Invite resent') ? 'text-emerald-700' : 'text-brick'}`}>
              {resendResult}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

export default function TeacherOverviewPanel() {
  const { user, profile, session } = useAuth()
  const teacherId = user?.id
  const accessToken = session?.access_token || null
  const {
    loading: balanceLoading,
    error: balanceError,
    totalCents: balanceCents,
  } = useTeacherBalance(accessToken, { role: profile?.role || null })

  // ===== Balance header
  const balanceDisplay = useMemo(() => {
    if (balanceCents == null || !Number.isFinite(balanceCents)) return '—'
    return (balanceCents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
  }, [balanceCents])

  const greetingName = useMemo(() => {
    if (profile?.first_name) return profile.first_name
    if (profile?.full_name) {
      const [first] = String(profile.full_name).split(' ')
      if (first) return first
    }
    if (user?.user_metadata?.full_name) {
      const [first] = String(user.user_metadata.full_name).split(' ')
      if (first) return first
    }
    if (user?.email) return user.email.split('@')[0]
    return undefined
  }, [profile?.first_name, profile?.full_name, user?.email, user?.user_metadata?.full_name])

  // ===== Reimbursements
  const [reims, setReims] = useState<Reimbursement[]>([])
  const [reimLoading, setReimLoading] = useState(false)
  const [reimError, setReimError] = useState<string | null>(null)

  const fetchReimbursements = useCallback(async () => {
    if (!teacherId) return
    setReimLoading(true)
    try {
      const { data, error } = await supabase
        .from('reimbursements')
        .select('*')
        .eq('teacher_id', teacherId)
        .order('created_at', { ascending: false })
        .limit(6)

      if (error) throw error
      setReims((data ?? []) as Reimbursement[])
      setReimError(null)
    } catch (error) {
      console.error('[TeacherOverview] reimbursements load failed', error)
      setReimError("We couldn't load your recent reimbursements.")
      setReims([])
    } finally {
      setReimLoading(false)
    }
  }, [teacherId])

  // ===== Photos
  const [photoPreviews, setPhotoPreviews] = useState<PhotoPreview[]>([])
  const [photosLoading, setPhotosLoading] = useState(false)
  const [photosError, setPhotosError] = useState<string | null>(null)

  const fetchPhotos = useCallback(async () => {
    if (!teacherId) return
    setPhotosLoading(true)
    try {
      const { data, error } = await supabase
        .from('teacher_photos')
        .select('*')
        .eq('teacher_id', teacherId)
        .order('created_at', { ascending: false })

      if (error) throw error
      const records = data ?? []

      const previewRecords = await Promise.all(
        records.slice(0, 6).map(async (photo) => {
          try {
            const { data: fileData, error: downloadError } = await supabase.storage
              .from('photos')
              .download(photo.file_url)

            if (downloadError || !fileData) {
              if (downloadError) console.warn('[TeacherOverview] photo download failed', downloadError)
              return { ...photo, previewUrl: null }
            }

            const previewUrl = URL.createObjectURL(fileData)
            return { ...photo, previewUrl }
          } catch (downloadError) {
            console.warn('[TeacherOverview] photo preview failed', downloadError)
            return { ...photo, previewUrl: null }
          }
        })
      )

      setPhotoPreviews(previewRecords as PhotoPreview[])
      setPhotosError(null)
    } catch (error) {
      console.error('[TeacherOverview] teacher photos load failed', error)
      setPhotoPreviews([])
      setPhotosError("We couldn't load your classroom stories.")
    } finally {
      setPhotosLoading(false)
    }
  }, [teacherId])

  useEffect(() => {
    if (!teacherId) return
    fetchReimbursements()
    fetchPhotos()
  }, [teacherId, fetchReimbursements, fetchPhotos])

  useEffect(() => {
    return () => {
      photoPreviews.forEach((item) => {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl)
      })
    }
  }, [photoPreviews])

  // ===== Students (roster) + Add Student modal
  const [students, setStudents] = useState<Student[]>([])
  const [studentsLoading, setStudentsLoading] = useState(false)
  const [studentsError, setStudentsError] = useState('')
  const [addOpen, setAddOpen] = useState(false)

  const loadStudents = useCallback(async () => {
    if (!teacherId) return
    setStudentsLoading(true)
    setStudentsError('')
    try {
      // Try RLS first
      const res = await supabase.from('students').select('id, full_name, grade, status, guardian_confirmed_at').order('full_name')
      if (res.error) {
        // Fallback to core-api (service role) if RLS blocks
        if (!accessToken) throw res.error
        const CORE_API = import.meta.env.VITE_CORE_API_URL || ''
        const raw = await jsonFetch(`${CORE_API}/api/v1/students`) as { data?: { students?: Student[] }; students?: Student[] }
        const wrapper = raw?.data ?? raw
        const roster = Array.isArray(wrapper?.students) ? wrapper.students : []
        setStudents(
          roster.map((student: Student) => ({
            ...student,
            full_name: student.full_name ?? student.display_name ?? 'Student',
          })),
        )
      } else {
        setStudents((res.data || []).map((s) => ({
          ...s,
          full_name: s.full_name ?? '',
          status: (s as Student).status ?? 'draft',
          guardian_confirmed_at: (s as Student).guardian_confirmed_at ?? null,
        })))
      }
    } catch (e) {
      console.warn('[TeacherOverview] students load failed', e)
      setStudents([])
      setStudentsError('Unable to load your roster right now.')
    } finally {
      setStudentsLoading(false)
    }
  }, [teacherId, accessToken])

  useEffect(() => {
    if (!teacherId) return
    loadStudents()
  }, [teacherId, loadStudents])

  const afterStudentAdded = useCallback(async () => {
    setAddOpen(false)
    await loadStudents()
  }, [loadStudents])

  if (!teacherId) {
    return (
      <div className="rounded-3xl bg-white/80 p-8 text-center text-cocoa/70">
        Sign in to see your personalized teacher overview.
      </div>
    )
  }

  return (
    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-amber-50 via-white to-orange-50/40 p-6">
      <img
        src="/illustrations/teacher-bg-warm.svg"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-20"
      />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/80 via-white/60 to-transparent" />
      <div className="relative space-y-6">
        {/* ===== Header / Balance ===== */}
        <section className="rounded-2xl bg-white/85 p-6 shadow-soft backdrop-blur">
          <h3 className="text-lg font-semibold text-cocoa">
            Welcome back{greetingName ? `, ${greetingName}` : ''}!
          </h3>
          <p className="mt-1 text-sm text-cocoa/80">
            {balanceLoading ? 'Checking your available classroom balance…' : 'Your available classroom balance is…'}
          </p>
          <div className="mt-3 text-3xl font-bold text-cocoa">{balanceDisplay}</div>
          {balanceError ? <p className="mt-2 text-sm text-brick">{balanceError}</p> : null}
        </section>

        <section className="rounded-2xl bg-white/85 p-6 shadow-soft backdrop-blur">
          <CurrentToolsCard role="teacher" manageHref="/portal/teacher/ai-tools" />
        </section>

        {/* ===== Classroom Settings Quick Link ===== */}
        <section className="rounded-2xl bg-white/85 p-4 shadow-soft backdrop-blur">
          <Link
            to="/portal/teacher/classroom-settings"
            className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-left transition hover:border-sky-300 hover:shadow-sm"
          >
            <div>
              <h4 className="text-sm font-semibold text-cocoa">Classroom Settings</h4>
              <p className="text-xs text-cocoa/60">School info, schedule, AI context</p>
            </div>
            <span className="text-sm text-sky-600">&rarr;</span>
          </Link>
        </section>

        {/* ===== Student Roster + Add Student ===== */}
        <section className="rounded-2xl bg-white/85 p-6 shadow-soft backdrop-blur">
          <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-lg font-semibold text-cocoa">Your Students</h3>
            <div className="flex items-center gap-2">
              <button
                className="rounded border border-sand px-3 py-1.5 text-sm text-cocoa/80 hover:bg-sand/20"
                onClick={loadStudents}
              >
                Refresh
              </button>
              <button
                className="rounded bg-brick px-3 py-1.5 text-sm font-semibold text-white hover:bg-brickDark"
                onClick={() => setAddOpen(true)}
              >
                + Add Student
              </button>
            </div>
          </div>

          {studentsLoading ? (
            <p className="text-sm text-cocoa/60">Loading roster…</p>
          ) : studentsError ? (
            <p className="text-sm text-brick">{studentsError}</p>
          ) : !students.length ? (
            <p className="text-sm text-cocoa/60">No students yet. Add your first student to get started.</p>
          ) : (
            <div className="space-y-2">
              {students.map((s) => (
                <StudentRow key={s.id} student={s} onRefresh={loadStudents} />
              ))}
            </div>
          )}
        </section>

        <ParentUpdatesCard />

        {/* ===== Reimbursements ===== */}
        <section className="rounded-2xl bg-white/85 p-6 shadow-soft backdrop-blur">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-lg font-semibold text-cocoa">Emergency Reimbursements</h3>
            <span className="text-sm text-cocoa/60">We’ve got your back.</span>
          </div>
          <UploadWidget
            className="mt-4"
            endpoint={`${CORE_API}/api/v1/teacher/reimbursements/upload`}
            teacherId={teacherId}
            accept=".jpg,.jpeg,.png,.pdf"
            helperText="Accepted files: JPG, PNG, or PDF"
            onUploaded={fetchReimbursements}
          />
          <ReimbursementsList items={reims} loading={reimLoading} error={reimError} />
        </section>

        {/* ===== Classroom Photos ===== */}
        <section className="rounded-2xl bg-white/85 p-6 shadow-soft backdrop-blur">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-lg font-semibold text-cocoa">Share Your Classroom Story</h3>
            <span className="text-sm text-cocoa/60">Your classroom, your story.</span>
          </div>
          <UploadWidget
            className="mt-4"
            endpoint={`${CORE_API}/api/v1/teacher/photos/upload`}
            teacherId={teacherId}
            accept="image/*"
            helperText="Share a classroom moment (images only)"
            withCaption
            onUploaded={fetchPhotos}
          />
          <PhotoStrip items={photoPreviews} loading={photosLoading} error={photosError} />
        </section>
      </div>

      {/* Add Student Modal */}
      <AddStudentModal open={addOpen} onClose={() => setAddOpen(false)} onAdded={afterStudentAdded} />
    </div>
  )
}

/* ========================= Helpers (unchanged from your file) ========================= */

interface UploadWidgetProps {
  className?: string;
  endpoint: string;
  teacherId: string | undefined;
  accept: string;
  helperText: string;
  withCaption?: boolean;
  onUploaded?: (payload: unknown) => void | Promise<void>;
}

function UploadWidget({
  className = '',
  endpoint,
  teacherId,
  accept,
  helperText,
  withCaption = false,
  onUploaded,
}: UploadWidgetProps) {
  const [file, setFile] = useState<File | null>(null)
  const [caption, setCaption] = useState('')
  const [dragActive, setDragActive] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [feedback, setFeedback] = useState<string | null>(null)
  const [feedbackType, setFeedbackType] = useState('success')

  const resetState = () => {
    setFile(null)
    setCaption('')
  }

  const handleFiles = (fileList: FileList | null | undefined) => {
    const nextFile = fileList?.[0]
    if (nextFile) {
      setFile(nextFile)
      setFeedback(null)
    }
  }

  const onDrop = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setDragActive(false)
    handleFiles(event.dataTransfer?.files)
  }

  const onDragOver = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault()
    event.stopPropagation()
    if (!dragActive) setDragActive(true)
  }

  const onDragLeave = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setDragActive(false)
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!file || !teacherId) return

    setSubmitting(true)
    setFeedback(null)

    try {
      const formData = new FormData()
      formData.append('file', file)
      if (withCaption && caption.trim()) {
        formData.append('caption', caption.trim())
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'x-pt-teacher-id': teacherId,
        },
        body: formData,
      })

      if (!response.ok) {
        const message = await response.text()
        throw new Error(message || 'Upload failed')
      }

      let payload = null
      try {
        payload = await response.json()
      } catch {
        payload = null
      }

      setFeedback('Uploaded successfully!')
      setFeedbackType('success')
      resetState()
      if (onUploaded) {
        try {
          await onUploaded(payload)
        } catch (callbackError) {
          console.warn('[UploadWidget] onUploaded callback failed', callbackError)
        }
      }
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : 'Something went wrong.')
      setFeedbackType('error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className={`${className} flex flex-col gap-3`}>
      <label
        className={`relative flex min-h[140px] cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-6 text-center transition focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-cocoa ${
          dragActive ? 'border-brick bg-brick/10 text-cocoa' : 'border-sand/80 bg-white/80 text-cocoa/80'
        } ${!teacherId ? 'cursor-not-allowed opacity-70' : ''}`}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
      >
        <input
          type="file"
          accept={accept}
          disabled={!teacherId}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          onChange={(event) => handleFiles(event.target.files)}
        />
        <div className="flex flex-col items-center gap-2">
          <span className="rounded-full bg-brick/10 px-3 py-1 text-xs font-medium text-brick">Drag & drop</span>
          <p className="text-sm font-semibold text-cocoa">or click to choose a file</p>
          <p className="text-xs text-cocoa/60">{file ? file.name : helperText}</p>
        </div>
      </label>

      {withCaption && (
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-cocoa/80" htmlFor={`${endpoint}-caption`}>
            Caption
          </label>
          <input
            id={`${endpoint}-caption`}
            type="text"
            value={caption}
            maxLength={140}
            disabled={!teacherId || submitting}
            onChange={(event) => setCaption(event.target.value)}
            placeholder="Add a short caption (140 characters max)…"
            className="w-full rounded-xl border border-sand/70 bg-white/90 px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none focus:ring-2 focus:ring-cocoa/20"
          />
          <span className="self-end text-[11px] text-cocoa/50">{caption.length}/140</span>
        </div>
      )}

      <div className="flex items-center gap-3">
        <button
          type="submit"
          className="rounded-full bg-brick px-4 py-2 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-60"
          disabled={!file || !teacherId || submitting}
        >
          {submitting ? 'Uploading…' : 'Upload'}
        </button>
        {feedback && (
          <span className={`text-sm ${feedbackType === 'error' ? 'text-brick' : 'text-emerald-700'}`}>
            {feedback}
          </span>
        )}
      </div>
    </form>
  )
}

interface ReimbursementsListProps {
  items?: Reimbursement[];
  loading?: boolean;
  error?: string | null;
}

function ReimbursementsList({ items = [], loading, error }: ReimbursementsListProps) {
  if (loading) {
    return <p className="mt-4 text-sm text-cocoa/60">Loading your recent reimbursements…</p>
  }

  if (error) {
    return <p className="mt-4 text-sm text-brick">{error}</p>
  }

  if (!items.length) {
    return <p className="mt-4 text-sm text-cocoa/60">No receipts yet. Upload your first emergency expense to get started.</p>
  }

  return (
    <ul className="mt-4 space-y-3">
      {items.map((item) => {
        const statusKey = String(item.status || 'submitted').toLowerCase()
        const badgeClass = STATUS_THEME[statusKey] || STATUS_THEME.submitted
        return (
          <li
            key={item.id}
            className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-sand/70 bg-white/90 px-4 py-3 text-sm text-cocoa"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium">{item.caption || 'Receipt uploaded'}</p>
              <p className="text-xs text-cocoa/60">{formatDate(item.created_at)}</p>
            </div>
            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${badgeClass}`}>
              {formatStatus(item.status)}
            </span>
          </li>
        )
      })}
    </ul>
  )
}

interface PhotoStripProps {
  items?: PhotoPreview[];
  loading?: boolean;
  error?: string | null;
}

function PhotoStrip({ items = [], loading, error }: PhotoStripProps) {
  if (loading) {
    return <p className="mt-4 text-sm text-cocoa/60">Loading your classroom stories…</p>
  }

  if (error) {
    return <p className="mt-4 text-sm text-brick">{error}</p>
  }

  if (!items.length) {
    return <p className="mt-4 text-sm text-cocoa/60">No stories yet. Share a quick snapshot from your classroom!</p>
  }

  return (
    <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
      {items.map((photo) => {
        const status = photo.approved ? 'approved' : 'pending'
        const badgeClass = PHOTO_STATUS_THEME[status]
        return (
          <figure key={photo.id} className="overflow-hidden rounded-xl border border-sand/70 bg-white/90 shadow-soft">
            {photo.previewUrl ? (
              <img
                src={photo.previewUrl}
                alt={photo.caption ? photo.caption : 'Classroom photo'}
                className="h-28 w-full object-cover"
              />
            ) : (
              <div className="flex h-28 w-full items-center justify-center bg-shell text-xs text-cocoa/40">Preview unavailable</div>
            )}
            <figcaption className="flex items-center justify-between gap-2 px-3 py-2 text-xs text-cocoa/70">
              <span className="truncate" title={photo.caption || 'Classroom photo'}>
                {photo.caption || 'Classroom photo'}
              </span>
              <span className={`whitespace-nowrap rounded-full px-2 py-0.5 font-semibold ${badgeClass}`}>
                {photo.approved ? 'Approved' : 'Pending'}
              </span>
            </figcaption>
          </figure>
        )
      })}
    </div>
  )
}
